import { useLiveQuery } from "dexie-react-hooks";
import { Calendar, Check, Flag, FolderInput, Tag, Trash2, X, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { Command } from "@todoer/shared";
import { db } from "../lib/db.js";
import { clearSelection, useSelection } from "../lib/selection.js";
import { cmd, dispatch } from "../lib/sync.js";
import { showToast } from "../lib/toast.js";
import { DuePicker } from "./DuePicker.js";
import { OptionList, Popover } from "./overlay.js";
import { PriorityList, ProjectList } from "./pickers.js";
import { cx, IconButton, useIsPhone } from "./ui.js";

type Pop = { kind: "date" | "priority" | "move" | "label"; anchor: HTMLElement } | null;

/** Actions for several selected tasks at once: a floating bar on desktop, an action sheet on phone. */
export function BulkBar() {
  const { ids } = useSelection();
  const [pop, setPop] = useState<Pop>(null);
  const labels = useLiveQuery(() => db.labels.toArray(), [], []);
  const isPhone = useIsPhone();

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape" && ids.length && !e.defaultPrevented) clearSelection(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [ids.length]);
  useEffect(() => { if (!ids.length) setPop(null); }, [ids.length]);

  if (!ids.length) return null;
  const n = ids.length;
  const plural = `${n} task${n > 1 ? "s" : ""}`;
  const each = async (make: (id: string) => Command | null, message: string) => {
    setPop(null);
    const commands = ids.map(make).filter((c): c is Command => !!c);
    if (commands.length) await dispatch(...commands);
    showToast(message);
    clearSelection();
  };
  const open = (kind: NonNullable<Pop>["kind"]) => (e: { currentTarget: HTMLElement }) => { const anchor = e.currentTarget; setPop((p) => (p?.kind === kind ? null : { kind, anchor })); };
  const actions: { key: string; label: string; icon: LucideIcon; run: (e: { currentTarget: HTMLElement }) => void; danger?: boolean }[] = [
    { key: "done", label: "Complete", icon: Check, run: () => void each((id) => cmd("item_complete", { id }), `Completed ${plural}`) },
    { key: "date", label: "Schedule", icon: Calendar, run: open("date") },
    { key: "priority", label: "Priority", icon: Flag, run: open("priority") },
    { key: "move", label: "Move", icon: FolderInput, run: open("move") },
    { key: "label", label: "Label", icon: Tag, run: open("label") },
    { key: "delete", label: "Delete", icon: Trash2, danger: true, run: () => void each((id) => cmd("item_delete", { id }), `Deleted ${plural}. Restore them from Trash.`) },
  ];

  return (
    <>
      <div role="toolbar" aria-label={`${n} selected`}
        className={cx("fixed z-40 border border-border bg-surface-raised text-text shadow-modal anim-pop",
          isPhone ? "inset-x-2 bottom-[calc(72px+env(safe-area-inset-bottom))] rounded-sheet p-2" : "bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-card p-1.5 pl-3")}>
        <div className={cx("flex items-center", isPhone && "mb-1 px-2")}>
          <span className="tabular text-body font-semibold">{n} selected</span>
          {isPhone && <IconButton icon={X} label="Clear selection" className="ml-auto" onClick={clearSelection} />}
        </div>
        {!isPhone && <div className="mx-2 h-5 w-px bg-border" aria-hidden />}
        <div className={cx(isPhone ? "grid grid-cols-6" : "flex items-center gap-0.5")}>
          {actions.map((a) => {
            const Icon = a.icon;
            return isPhone ? (
              <button key={a.key} type="button" onClick={a.run} className={cx("flex h-14 flex-col items-center justify-center gap-1 rounded-control text-caption font-medium normal-case tracking-normal hover:bg-hover", a.danger ? "text-danger" : "text-text-muted")}>
                <Icon size={20} aria-hidden />{a.label}
              </button>
            ) : (
              <button key={a.key} type="button" onClick={a.run} aria-expanded={pop?.kind === a.key ? true : undefined}
                className={cx("inline-flex h-8 items-center gap-1.5 rounded-control px-2.5 text-body font-medium transition-colors", a.danger ? "text-danger hover:bg-danger-soft" : "text-text-muted hover:bg-hover hover:text-text", pop?.kind === a.key && "bg-hover text-text")}>
                <Icon size={16} aria-hidden />{a.label}
              </button>
            );
          })}
        </div>
        {!isPhone && <><div className="mx-1 h-5 w-px bg-border" aria-hidden /><IconButton icon={X} label="Clear selection" shortcut="Esc" onClick={clearSelection} /></>}
      </div>
      {pop?.kind === "date" && (
        <Popover anchor={pop.anchor} onClose={() => setPop(null)} title={`Schedule ${plural}`} width={300}>
          <DuePicker value={null} onChange={(due) => void each((id) => cmd("item_update", { id, due }), `Rescheduled ${plural}`)} />
        </Popover>
      )}
      {pop?.kind === "priority" && (
        <Popover anchor={pop.anchor} onClose={() => setPop(null)} title="Priority" width={200}>
          <PriorityList value={1} onSelect={(p) => void each((id) => cmd("item_update", { id, priority: p }), "Priority updated")} />
        </Popover>
      )}
      {pop?.kind === "move" && (
        <Popover anchor={pop.anchor} onClose={() => setPop(null)} title={`Move ${plural}`} width={260}>
          <ProjectList withSections value={null} onSelect={(pid, sid) => { if (pid) void each((id) => cmd("item_move", { id, projectId: pid, sectionId: sid, parentId: null }), `Moved ${plural}`); }} />
        </Popover>
      )}
      {pop?.kind === "label" && (
        <Popover anchor={pop.anchor} onClose={() => setPop(null)} title="Add label" width={240}>
          <OptionList label="Add label" search={labels.length > 6} emptyText="No labels yet" options={labels.map((l) => ({
            key: l.id, label: `@${l.name}`, icon: Tag, onSelect: async () => {
              const list = await db.tasks.where("id").anyOf(ids).toArray();
              void each((id) => { const t = list.find((x) => x.id === id); return t && !t.labelIds.includes(l.id) ? cmd("item_update", { id, labelIds: [...t.labelIds, l.id] }) : null; }, `Added @${l.name}`);
            },
          }))} />
        </Popover>
      )}
    </>
  );
}
